import React from 'react';
import { Check } from 'lucide-react';
import { useAnalysisWorkflow } from '../hooks/useAnalysisWorkflow';
import '../../../styles/origin-theme.css';

/**
 * StepNavigator - Progress indicator for the analysis workflow
 * Shows current step, completed steps, and allows navigation to previous steps
 */
export const StepNavigator: React.FC = () => {
  const { steps, currentStep, getStepStatus, jumpToStep } = useAnalysisWorkflow();

  const handleStepClick = (stepId: number) => {
    const status = getStepStatus(stepId);
    if (status === 'completed' || status === 'active') {
      jumpToStep(stepId);
    }
  };

  return (
    <div className="w-full bg-[var(--bg-secondary)] border-b border-[var(--border-primary)] py-6 px-8">
      <div className="max-w-7xl mx-auto">
        {/* Progress Bar */}
        <div className="relative mb-8">
          {/* Background line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-[var(--border-primary)]" />

          {/* Progress line */}
          <div
            className="absolute top-5 left-0 h-0.5 bg-[var(--origin-blue-500)] transition-all duration-500"
            style={{
              width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
            }}
          />

          {/* Steps */}
          <div className="relative flex justify-between">
            {steps.map((step, index) => {
              const status = getStepStatus(step.id);
              const isClickable = status === 'completed' || status === 'active';

              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(step.id)}
                  disabled={!isClickable}
                  className={`
                    relative flex flex-col items-center group transition-all duration-200
                    ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                  `}
                >
                  {/* Circle */}
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      border-2 transition-all duration-200 z-10
                      ${
                        status === 'completed'
                          ? 'bg-[var(--success)] border-[var(--success)] text-white'
                          : status === 'active'
                          ? 'bg-[var(--origin-blue-500)] border-[var(--origin-blue-500)] text-white shadow-[var(--shadow-blue)]'
                          : status === 'upcoming'
                          ? 'bg-[var(--bg-tertiary)] border-[var(--border-secondary)] text-[var(--text-tertiary)]'
                          : 'bg-[var(--bg-tertiary)] border-[var(--border-primary)] text-[var(--text-disabled)] opacity-50'
                      }
                      ${isClickable ? 'hover:scale-110' : ''}
                    `}
                  >
                    {status === 'completed' ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-semibold">{step.id}</span>
                    )}
                  </div>

                  {/* Step label */}
                  <div className="mt-3 text-center">
                    <div
                      className={`
                        text-sm font-medium transition-colors
                        ${
                          status === 'active'
                            ? 'text-[var(--origin-blue-500)]'
                            : status === 'completed'
                            ? 'text-[var(--text-primary)]'
                            : 'text-[var(--text-tertiary)]'
                        }
                      `}
                    >
                      {step.shortLabel}
                    </div>
                    <div className="text-xs text-[var(--text-tertiary)] mt-1 max-w-[120px]">
                      {step.description}
                    </div>
                  </div>

                  {/* Pulse animation for active step */}
                  {status === 'active' && (
                    <div
                      className="absolute top-0 left-0 w-10 h-10 rounded-full animate-ping
                                 bg-[var(--origin-blue-500)] opacity-20"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Current step title */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-[var(--text-primary)]">
            {steps.find((s) => s.id === currentStep)?.label}
          </h2>
        </div>
      </div>
    </div>
  );
};

export default StepNavigator;
